import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Centralized number formatting utilities for consistent display across the application
 */

/**
 * Format currency values with proper decimal places and locale formatting
 * @param amount - The amount to format (in dollars, not cents)
 * @param currency - The currency code (default: 'USD')
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(
  amount: number | undefined | null,
  currency: string = "USD",
  locale: string = "en-US"
): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
    }).format(0);
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount);
}

/**
 * Format currency values when the input is in cents
 * @param amountInCents - The amount in cents to format
 * @param currency - The currency code (default: 'USD')
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrencyFromCents(
  amountInCents: number | undefined | null,
  currency: string = "USD",
  locale: string = "en-US"
): string {
  if (amountInCents === undefined || amountInCents === null || isNaN(amountInCents)) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
    }).format(0);
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amountInCents / 100);
}

/**
 * Format percentage values with consistent decimal precision
 * @param value - The percentage value (0-100 or 0-1 depending on isDecimal)
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @param isDecimal - Whether the input is a decimal (0-1) or percentage (0-100) (default: false)
 * @returns Formatted percentage string (e.g., "12.5%")
 */
export function formatPercentage(
  value: number | undefined | null,
  decimalPlaces: number = 1,
  isDecimal: boolean = false
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return "0%";
  }

  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimalPlaces)}%`;
}

/**
 * Format large numbers with proper comma separators
 * @param value - The number to format
 * @param decimalPlaces - Number of decimal places to show (default: 0)
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted number string (e.g., "1,234,567")
 */
export function formatNumber(
  value: number | undefined | null,
  decimalPlaces: number = 0,
  locale: string = "en-US"
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return "0";
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
}

/**
 * Format file sizes in human-readable format
 * @param bytes - The size in bytes
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number, decimalPlaces: number = 1): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimalPlaces))} ${sizes[i]}`;
}

/**
 * Alias for formatFileSize for backward compatibility
 */
export function formatBytes(bytes: number, decimalPlaces: number = 1): string {
  return formatFileSize(bytes, decimalPlaces);
}

/**
 * Format compact numbers for display in limited space (e.g., 1.2K, 1.5M)
 * @param value - The number to format
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns Formatted compact number string (e.g., "1.2K")
 */
export function formatCompactNumber(
  value: number | undefined | null,
  decimalPlaces: number = 1
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return "0";
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1000000000) {
    return `${sign}${(absValue / 1000000000).toFixed(decimalPlaces)}B`;
  } else if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toFixed(decimalPlaces)}M`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toFixed(decimalPlaces)}K`;
  } else {
    return `${sign}${absValue.toFixed(decimalPlaces)}`;
  }
}
